import { Text } from "@src/lib/ui-react";
import { HCF_USER_EVENTS } from "src/constants/analyticsEvents";
import { unLinkUser } from "src/containers/facilityUser/api";
import {
  LOADING_STATE,
  PROCESS_MODAL_STATE,
  ROLE_MAPPINGS,
} from "src/containers/teamMembers/constants";
import { generateLogData } from "src/containers/teamMembers/logs";
import { TeamMemberActions } from "src/containers/teamMembers/teamMemberReducer";
import { ITeamMember } from "src/containers/teamMembers/useTeamMembersAPIHook";
import { Button } from "src/designsystem/Button/Button";
import { SessionType } from "src/modules/interface";
import { getErrorMessage } from "src/utils/errors";
import { logEvent } from "@src/appV2/lib/analytics";
import { Dispatch, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { isDefined } from "@clipboard-health/util-ts";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { Close as CloseIcon } from "@mui/icons-material";

const unlink = async ({
  dispatch,
  member,
  userId,
  admin,
  adminUserId,
  type,
  workplaceId,
  getTeamMembers,
}) => {
  const event = generateLogData(userId, admin, adminUserId, type, workplaceId, member._id);
  try {
    await unLinkUser({
      userId: member._id,
      facilityId: userId,
    });
    logEvent(HCF_USER_EVENTS.UNLINK_TEAM_MEMBER, {
      ...event,
      success: true,
    });
    dispatch({ type: "SET_MODAL_MEMBER", member: undefined });
    dispatch({ type: "CLEAR_TEAM_MEMBERS" });
    getTeamMembers(workplaceId, false);
  } catch (error) {
    showErrorToast(getErrorMessage(error));
    logEvent(HCF_USER_EVENTS.UNLINK_TEAM_MEMBER, {
      ...event,
      success: false,
    });
  }
};

type TeamMemberStatusModalProps = {
  member?: ITeamMember;
  dispatch: Dispatch<TeamMemberActions>;
  getTeamMembers: (id: string, isArchived: boolean) => void;
  visibility: boolean;
};

export function TeamMemberUnlinkModal(props: TeamMemberStatusModalProps) {
  const { member, dispatch, getTeamMembers, visibility } = props;
  const {
    admin,
    adminUserId,
    userId,
    type,
    profile: { userId: workplaceId, name },
  } = useSelector((state: SessionType) => state.session);
  const [isLoading, setIsLoading] = useState<LOADING_STATE>();

  const handleUnlink = async () => {
    setIsLoading(LOADING_STATE.ACTIVATE_OR_DEACTIVATE);
    await unlink({
      dispatch,
      member,
      userId,
      admin,
      adminUserId,
      type,
      workplaceId,
      getTeamMembers,
    });
    setIsLoading(undefined);
    dispatch({ type: "SET_UNLINK_MODAL_VISIBILITY", unlinkModal: false });
    dispatch({
      type: "SET_PROCESS_MODAL_VISIBILITY",
      processModal: PROCESS_MODAL_STATE.off,
    });
  };

  if (!member) {
    return null;
  }

  const memberRoles = member.roles.map((role) => ROLE_MAPPINGS[role].label).join(", ");

  return (
    <Dialog
      open={visibility}
      maxWidth="md"
      disableEscapeKeyDown={isLoading === LOADING_STATE.ACTIVATE_OR_DEACTIVATE}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Text variant="h3">Unlink Account</Text>
          <CloseIcon
            aria-label="Close"
            onClick={() => {
              dispatch({ type: "SET_UNLINK_MODAL_VISIBILITY", unlinkModal: false });
            }}
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers={true}>
        <Stack spacing={1}>
          <Text variant="body2">
            <b>
              {member.name} (role: {memberRoles})
            </b>{" "}
            will lose access to {name}, but will maintain access to other facilities:
          </Text>
          <Stack>
            {member.facilities
              ?.filter((facility) => isDefined(facility?.userId) && facility.userId !== workplaceId)
              .map((facility) => (
                <Stack direction="row" key={facility.userId} sx={{ paddingLeft: 1 }} spacing={1}>
                  <div>●</div>
                  <Text variant="body2">{facility.name}</Text>
                </Stack>
              ))}
          </Stack>
          <Text variant="body2">Are you sure you want to unlink from {name}?</Text>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ paddingX: 3, padding: 2 }}>
        <Button
          variant="secondary"
          onClick={handleUnlink}
          isLoading={isLoading === LOADING_STATE.ACTIVATE_OR_DEACTIVATE}
          key={1}
        >
          Unlink
        </Button>
      </DialogActions>
    </Dialog>
  );
}
